<template>
  <div class="agent-management">
    <!-- Flash Message Component -->
    <FlashMessage ref="flashMessage" />
    
    <!-- Loading Indicator -->
    <LoadingIndicator v-if="loading" message="Loading agents..." />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="confirm-modal">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete <strong>{{ agentToDelete?.name }}</strong>?
          This action cannot be undone.
        </p>
        <div class="confirm-buttons">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Cancel
          </button>
          <button @click="deleteAgent" class="btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <!-- aprove confirmation modal -->
    <!-- <div v-if="showDeleteModal" class="modal-overlay">
      <div class="confirm-modal">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete <strong>{{ agentToDelete?.name }}</strong>?
          This action cannot be undone.
        </p>
        <div class="confirm-buttons">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Cancel
          </button>
          <button @click="deleteAgent" class="btn-danger">Delete</button>
        </div>
      </div>
    </div> -->

    <!-- Header with title and add agent button -->
    <div class="header">
      <h1>
        <i class="fas fa-users"></i> 
        EKU Agents
        <span v-if="totalFilteredCount" class="agent-count">
          ({{ totalFilteredCount }} total)
        </span>
      </h1>
      <button class="btn-primary" @click="showAddModal = true">
        <i class="fas fa-plus"></i> Register Agent
      </button>
    </div>

    <!-- Search and Filter Bar -->
    <div class="control-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search by name, email, phone, or store..."
          @input="applyFilters"
        />
        <i 
          v-if="searchQuery" 
          @click="searchQuery = ''" 
          class="fas fa-times clear-icon"
        ></i>
      </div>

      <div class="filters">
        <!-- Status Filter -->
        <select v-model="statusFilter" @change="applyFilters">
          <option value="all">All Statuses</option>
          <option value="approved">Approved</option>
          <option value="suspended">Suspended</option>
        </select>

        <!-- Products Filter -->
        <select v-model="productCountFilter" @change="applyFilters">
          <option value="all">All Agents</option>
          <option value="has-products">Has Products</option>
          <option value="no-products">No Products</option>
        </select>

        <!-- Reset Filters Button -->
        <button 
          v-if="isFilterActive" 
          class="btn-secondary" 
          @click="resetFilters"
        >
          <i class="fas fa-sync-alt"></i> Reset
        </button>
      </div>
    </div>

    <!-- Active Filters Display -->
    <div v-if="isFilterActive" class="active-filters">
      <span v-if="statusFilter !== 'all'" class="filter-tag">
        Status: {{ statusFilter }}
        <i @click="statusFilter = 'all'" class="fas fa-times"></i>
      </span>
      <span v-if="productCountFilter !== 'all'" class="filter-tag">
        Products: {{ productCountFilter === 'has-products' ? 'Has Products' : 'No Products' }}
        <i @click="productCountFilter = 'all'" class="fas fa-times"></i>
      </span>
      <span v-if="searchQuery" class="filter-tag">
        Search: "{{ searchQuery }}"
        <i @click="searchQuery = ''" class="fas fa-times"></i>
      </span>
    </div>

    <!-- Agents Table -->
    <div class="table-responsive">
      <table class="agents-table">
        <thead>
          <tr>
            <th @click="sortBy('id')">
              ID <i :class="sortIcon('id')"></i>
            </th>
            <th>Photo</th>
            <th @click="sortBy('name')">
              Agent Name <i :class="sortIcon('name')"></i>
            </th>
            <th @click="sortBy('email')">
              Email <i :class="sortIcon('email')"></i>
            </th>
            <th @click="sortBy('phone')">
              Phone <i :class="sortIcon('phone')"></i>
            </th>
            <th>Store Name</th>
            <th @click="sortBy('products_count')">
              Products <i :class="sortIcon('products_count')"></i>
            </th>
            <th @click="sortBy('status')">
              Status <i :class="sortIcon('status')"></i>
            </th>
            <th>Registered By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody v-if="!loading">
          <tr v-for="agent in filteredAgents" :key="agent.id">
            <td class="agent-id">{{ agent.id }}</td>
            <td>
              <img
                :src="getFullImageUrl(agent.imgpath)"
                :alt="agent.name"
                class="thumbnail"
                @error="handleAgentImageError"
              />
            </td>
            <!-- <td>image</td> -->
            <td class="agent-name">
              {{ agent.name }}
              <!-- <span v-if="agent.agent_name" class="agent-store-badge">
                {{ agent.agent_name }}
              </span> -->
            </td>
            <td class="agent-email">{{ agent.email }}</td>
            <td class="agent-phone">{{ formatPhoneNumber(agent.phone) }}</td>
            <td>
              <span v-if="agent.agent_name" class="store-name">
                <i class="fas fa-store"></i> {{ truncateStoreName(agent.agent_name) }}
              </span>
              <span v-else class="no-store">—</span>
            </td>
            <td>
              <span 
                :class="`product-count-badge ${agent.products_count > 0 ? 'has-products' : 'no-products'}`"
                :title="agent.products_count > 0 ? `${agent.products_count} products listed` : 'No products yet'"
              >
                <i class="fas fa-box"></i> {{ agent.products_count }}
              </span>
            </td>
            <td>
              <span :class="`status-badge agent-${agent.status}`">
                <i :class="agent.status === 'approved' ? 'fas fa-check-circle' : 'fas fa-ban'"></i>
                {{ agent.status }}
              </span>
            </td>
            <td class="registered-by">
              <i class="fas fa-user-check"></i> {{ agent.registered_by_name }}
              <span class="registered-date" :title="agent.registered_date">
                ({{ formatRelativeDate(agent.registered_date) }})
              </span>
            </td>
            <td class="actions">
              <button
                @click="viewAgentDetails(agent)"
                class="btn-icon"
                title="View Details"
              >
                <i class="fas fa-eye"></i>
              </button>
              <button
                @click="editAgent(agent)"
                class="btn-icon"
                title="Edit Agent"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                @click="toggleAgentStatus(agent)"
                class="btn-icon"
                :title="agent.status === 'suspended' ? 'Approve Agent' : 'Suspend Agent'"
              >
                <i
                  :class="
                    agent.status === 'approved'
                      ? 'fas fa-toggle-on text-success'
                      : 'fas fa-toggle-off text-danger'
                  "
                ></i>
              </button>
              <button
                @click="resetAgentPassword(agent)"
                class="btn-icon"
                title="Reset Password"
              >
                <i class="fas fa-key"></i>
              </button>
              <button
                @click="confirmDelete(agent)"
                class="btn-icon btn-danger-icon"
                title="Delete Agent"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr>
            <td colspan="10" class="loading-row">
              <LoadingIndicator message="Loading agents..." />
            </td>
          </tr>
        </tbody>
       </table>
      
      <!-- Empty State -->
      <div v-if="filteredAgents.length === 0 && !loading" class="empty-state">
        <i class="fas fa-users-slash"></i>
        <p>No agents found.</p>
        <p class="empty-hint">
          Try adjusting your filters or 
          <button @click="showAddModal = true" class="link-btn">register a new agent</button>.
        </p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <button
        @click="changePage(currentPage - 1)"
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-chevron-left"></i> Previous
      </button>
      
      <div class="pagination-pages">
        <button
          v-for="page in displayedPages"
          :key="page"
          @click="changePage(page)"
          :class="{ active: currentPage === page }"
          class="pagination-page"
        >
          {{ page }}
        </button>
      </div>
      
      <button
        @click="changePage(currentPage + 1)"
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        Next <i class="fas fa-chevron-right"></i>
      </button>
    </div>
    
    <!-- Page Info -->
    <div v-if="totalFilteredCount > 0" class="pagination-info">
      Showing {{ ((currentPage - 1) * itemsPerPage) + 1 }} 
      to {{ Math.min(currentPage * itemsPerPage, totalFilteredCount) }} 
      of {{ totalFilteredCount }} agents
    </div>

    <!-- Export Actions -->
    <div class="export-actions" v-if="filteredAgents.length > 0">
      <button @click="exportToCSV" class="btn-export">
        <i class="fas fa-file-csv"></i> CSV
      </button>
      <button @click="exportToExcel" class="btn-export">
        <i class="fas fa-file-excel"></i> Excel
      </button>
      <button @click="printTable" class="btn-export">
        <i class="fas fa-print"></i> Print
      </button>
      <button @click="exportToPDF" class="btn-export">
        <i class="fas fa-file-pdf"></i> PDF
      </button>
    </div>

    <!-- Agent Form Modal -->
    <AgentForm
      v-if="showAddModal"
      :show="showAddModal"
      :editingAgent="editingAgent"
      :apiCategories="apiCategories"
      :initialFormData="formData"
      @close="closeModal"
      @submit="handleFormSubmit"
    />

    <!-- Agent Details Modal (Add this if you have one) -->
    <AgentDetailsModal
      v-if="showDetailsModal"
      :agent="selectedAgent"
      @close="showDetailsModal = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import { createAgent, deleteUser, fetchAgents, PasswordReset, ToggleAgentStatus } from "../../API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import profileavator from '@/assets/profile.png';
import AgentForm from "@/components/AgentForm.vue";



// ==================== STATE MANAGEMENT ====================

// Data stores
const agents = ref([]);
const loading = ref(true);
const flashMessage = ref(null);

// Filter state
const searchQuery = ref("");
const statusFilter = ref("all");
const dateFilter = ref("");
const productCountFilter = ref("all");
const sortField = ref("id");
const sortDirection = ref("asc");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalFilteredCount = ref(0);

// Modal state
const showAddModal = ref(false);
const showDeleteModal = ref(false);
const showDetailsModal = ref(false);
const editingAgent = ref(null);
const agentToDelete = ref(null);
const selectedAgent = ref(null);

// Form state (simplified for agents)
const formData = ref({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  agent_name: "",
  photo: null,
});

// ==================== HELPER FUNCTIONS ====================

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-TZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getFullImageUrl = (path) => {
  if (!path) return profileavator;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/storage')) return `${import.meta.env.VITE_API_URL}${path}`;
  return `${import.meta.env.VITE_API_URL}/storage/${path}`;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '—';
  // Format Tanzanian phone numbers
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3');
  }
  if (cleaned.length === 12 && cleaned.startsWith('255')) {
    return '0' + cleaned.slice(3).replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3');
  }
  return phone;
};

const formatRelativeDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

const truncateStoreName = (name, maxLength = 20) => {
  if (!name) return '';
  return name.length > maxLength ? name.slice(0, maxLength) + '...' : name;
};

const handleAgentImageError = (event) => {
  event.target.src = '/assets/default-avatar.png';
};

// ==================== COMPUTED PROPERTIES ====================

const filteredAgents = computed(() => {
  let result = [...agents.value];

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter((agent) =>
      agent.name.toLowerCase().includes(query) ||
      agent.email.toLowerCase().includes(query) ||
      agent.phone.includes(query) ||
      (agent.agent_name && agent.agent_name.toLowerCase().includes(query)) ||
      agent.id.toString().includes(query)
    );
  }

  // Apply status filter
  if (statusFilter.value && statusFilter.value !== 'all') {
    result = result.filter((agent) => agent.status === statusFilter.value);
  }

  // Apply date filter
  if (dateFilter.value) {
    const filterDate = new Date(dateFilter.value).toDateString();
    result = result.filter((agent) => {
      const agentDate = new Date(agent.registered_date).toDateString();
      return agentDate === filterDate;
    });
  }

  // Apply product count filter
  if (productCountFilter.value === 'has-products') {
    result = result.filter((agent) => agent.products_count > 0);
  } else if (productCountFilter.value === 'no-products') {
    result = result.filter((agent) => agent.products_count === 0);
  }

  // Apply sorting
  result.sort((a, b) => {
    let aVal = a[sortField.value];
    let bVal = b[sortField.value];
    
    if (aVal === undefined || aVal === null) aVal = '';
    if (bVal === undefined || bVal === null) bVal = '';
    
    if (sortField.value === 'products_count' || sortField.value === 'id') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDirection.value === "asc" 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    }
    
    if (aVal < bVal) return sortDirection.value === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection.value === "asc" ? 1 : -1;
    return 0;
  });

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const paginatedResult = result.slice(start, start + itemsPerPage.value);
  totalFilteredCount.value = result.length;
  
  return paginatedResult;
});

const totalPages = computed(() => {
  return Math.ceil(totalFilteredCount.value / itemsPerPage.value);
});

const isFilterActive = computed(() => {
  return searchQuery.value !== '' ||
         statusFilter.value !== 'all' ||
         productCountFilter.value !== 'all' ||
         dateFilter.value !== '';
});

const displayedPages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 2;
  const range = [];
  
  if (total <= 7) {
    for (let i = 1; i <= total; i++) range.push(i);
    return range;
  }
  
  range.push(1);
  
  if (current - delta > 2) range.push('...');
  
  for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
    range.push(i);
  }
  
  if (current + delta < total - 1) range.push('...');
  
  if (total !== 1) range.push(total);
  
  return range;
});

// ==================== FILTER METHODS ====================

const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const sortIcon = (field) => {
  if (sortField.value !== field) return 'fas fa-sort';
  return sortDirection.value === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
};

const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const resetFilters = () => {
  searchQuery.value = '';
  statusFilter.value = 'all';
  dateFilter.value = '';
  productCountFilter.value = 'all';
  currentPage.value = 1;
};

const applyFilters = () => {
  currentPage.value = 1;
};

// ==================== CRUD OPERATIONS ====================

const loadInitialData = async () => {
  try {
    loading.value = true;
    
    const response = await fetchAgents();
    
    console.log('API Response:', response);
    
    // Extract agents array from the response
    let agentsArray = [];
    if (response?.data?.agents && Array.isArray(response.data.agents)) {
      agentsArray = response.data.agents;
    } else if (response?.agents && Array.isArray(response.agents)) {
      agentsArray = response.agents;
    } else if (Array.isArray(response)) {
      agentsArray = response;
    } else {
      console.warn('Unexpected response structure:', response);
      agentsArray = [];
    }

    console.log('Agents array:', agentsArray);

    // Map agents to table format
    agents.value = agentsArray.map((agent) => ({
      id: agent.id,
      name: `${agent.first_name} ${agent.last_name}`,
      first_name: agent.first_name,
      last_name: agent.last_name,
      email: agent.email,
      phone: agent.phone,
      imgpath: agent.photo || null,
      agent_name: agent.agent_name || null,
      status: agent.is_approved === 1 ? "approved" : "suspended",
      registered_by: agent.registered_by,
      registered_by_name: "Staff", // You can map this if you have staff names
      registered_date: formatDate(agent.created_at),
      approved_at: agent.approved_at,
      suspension_reason: agent.suspension_reason || null,
      products_count: agent.products_count || 0,
      created_at: agent.created_at,
      updated_at: agent.updated_at,
      _original: {
        user_category_id: agent.user_category_id,
        is_approved: agent.is_approved,
        registered_by: agent.registered_by,
        photo: agent.photo,
        email_verified_at: agent.email_verified_at,
      },
    }));

    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("success", "Data loaded successfully");
    }
  } catch (error) {
    console.error("Error loading data:", error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("error", "Failed to load data");
    }
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  formData.value = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    agent_name: "",
    photo: null,
  };
};

const closeModal = () => {
  showAddModal.value = false;
  showDetailsModal.value = false;
  editingAgent.value = null;
  resetForm();
};

const editAgent = (agent) => {
  editingAgent.value = agent;
  formData.value = {
    first_name: agent.first_name,
    last_name: agent.last_name,
    email: agent.email,
    phone: agent.phone,
    agent_name: agent.agent_name || "",
    photo: agent.imgpath,
  };
  showAddModal.value = true;
};

const viewAgentDetails = (agent) => {
  selectedAgent.value = agent;
  showDetailsModal.value = true;
};

const confirmDelete = (agent) => {
  agentToDelete.value = agent;
  showDeleteModal.value = true;
};

const deleteAgent = async () => {
  try {
    loading.value = true;
    const response = await deleteUser(agentToDelete.value.id);
    agents.value = agents.value.filter((a) => a.id !== agentToDelete.value.id);
    
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("success", response.message);
    }
  } catch (error) {
    console.error("Error deleting agent:", error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("error", "Failed to delete agent");
    }
  } finally {
    loading.value = false;
    showDeleteModal.value = false;
    agentToDelete.value = null;
  }
};



// const deleteProduct = async () => {
//   try {
//     loading.value = true;
//     await apiDeleteProduct(productToDelete.value.id);
//     products.value = products.value.filter(
//       (p) => p.id !== productToDelete.value.id
//     );
//     flashMessage.value.showFlashMessage(
//       "success",
//       "Product deleted successfully"
//     );
//   } catch (error) {
//     console.error("Error deleting product:", error);
//     flashMessage.value.showFlashMessage("error", "Failed to delete product");
//   } finally {
//     loading.value = false;
//     showDeleteModal.value = false;
//     productToDelete.value = null;
//   }
// };


const toggleAgentStatus = async (agent) => {
  const action = agent.status === 'approved' ? 'suspend' : 'approve';
  const confirmed = confirm(`Are you sure you want to ${action} ${agent.name}?`);
  
  if (!confirmed) return;
  
  try {
    // Call your API to toggle status
    const response = await ToggleAgentStatus(agent.id);

    
    // Update local state
    agent.status = agent.status === 'approved' ? 'suspended' : 'approved';
    
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("success", response.message);
    }
  } catch (error) {
    console.error('Failed to update agent status:', error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("error", "Failed to update agent status");
    }
  }
};



const resetAgentPassword = async (agent) => {
  const confirmed = confirm(`Reset password for ${agent.name}? A new 6-digit password will be sent via SMS.`);
  if (!confirmed) return;
  
  try {
    // Call your API to reset password
    const response = await PasswordReset(agent.id);
    
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("success", response.message);
    }
  } catch (error) {
    console.error('Failed to reset password:', error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("error", "Failed to reset password");
    }
  }
};

const handleFormSubmit = async (formData) => {
  try {
    loading.value = true;

    if (editingAgent.value) {
      // await updateAgent(editingAgent.value.id, formData);
      if (flashMessage.value) {
        flashMessage.value.showFlashMessage("success", "Agent updated successfully");
      }
    } else {
      const response = await createAgent(formData);
      if (flashMessage.value) {
        flashMessage.value.showFlashMessage("success", "Agent created successfully");
      }
    }
    closeModal();
    await loadInitialData();
  } catch (error) {
    console.error("Form submission error:", error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("failure", error.message || "Failed to save agent");
    }
  } finally {
    loading.value = false;
  }
};

// Export functions
const exportToCSV = () => {
  const headers = ['ID', 'Name', 'Email', 'Phone', 'Store Name', 'Status', 'Registered Date'];
  const rows = filteredAgents.value.map(agent => [
    agent.id,
    agent.name,
    agent.email,
    agent.phone,
    agent.agent_name || '',
    agent.status,
    agent.registered_date
  ]);
  
  const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `agents_export_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportToExcel = () => {
  console.log('Excel export - implement with xlsx library');
};

const printTable = () => {
  window.print();
};

const exportToPDF = () => {
  console.log('PDF export - implement with jspdf library');
};

// ==================== LIFECYCLE ====================

onMounted(async () => {
  await loadInitialData();
});
</script>

<style scoped>
.agent-management {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
}

.control-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.search-box input:focus {
  outline: none;
  border-color: #3498db;
}

.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filters select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.agents-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.agents-table th,
.agents-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.agents-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.agents-table th:hover {
  background-color: #f1f3f5;
}

.agents-table tr:hover td {
  background-color: #f8f9fa;
}

.thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.draft {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.archived {
  background-color: #f8d7da;
  color: #721c24;
}

.low-stock {
  color: #dc3545;
  font-weight: 500;
}

.stock-warning {
  display: inline-block;
  width: 18px;
  height: 18px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  text-align: center;
  line-height: 18px;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
}

.btn-danger:hover {
  background-color: #c0392b;
}

.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #f8f9fa;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background-color: #e9ecef;
  color: #2c3e50;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.modal-overlay {
  position: fixed; /* Ensure the modal overlays on top */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 9999; /* Ensure it appears on top of all other content */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.confirm-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Add a subtle shadow for depth */
  animation: fadeIn 0.3s ease-out; /* Smooth fade-in effect */
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.confirm-buttons button {
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .agents-table th,
  .agents-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  .actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .agent-management {
    padding: 1rem;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}

/* Add export button styles */
.export-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.btn-export {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-export:hover {
  background-color: #f5f5f5;
}

.btn-export i {
  font-size: 1rem;
}
</style>
